const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

/** GIF sai inteiro: redesenhar num canvas achataria a animação no primeiro
 *  quadro. */
const SKIP_TYPES = ['image/gif'];

async function loadBitmap(file) {
  // `imageOrientation: 'from-image'` aplica o EXIF: sem isso a foto tirada com
  // o aparelho deitado é gravada de lado, porque o canvas ignora a tag de
  // rotação que o visualizador do celular respeita.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Safari antigo não aceita as opções — cai no <img> abaixo.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Reduz a foto para um tamanho que caiba no limite de upload. A câmera de um
 * celular atual entrega de 3MB a 12MB e 4000px de largura — mandar isso para
 * uma miniatura de cardápio esbarraria no limite de 5MB e faria o botão "Tirar
 * foto" parecer quebrado, além de gastar o 4G do estabelecimento.
 *
 * Devolve o arquivo original se ele já estiver pequeno, se for GIF, ou se algo
 * falhar no caminho: comprimir é otimização, não pode impedir o envio.
 */
export async function compressImage(file, { maxDimension = MAX_DIMENSION, quality = JPEG_QUALITY } = {}) {
  if (!file.type.startsWith('image/') || SKIP_TYPES.includes(file.type)) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDimension / Math.max(width, height));

    // Já pequena e leve: reencodar só perderia qualidade à toa.
    if (scale === 1 && file.size <= 1024 * 1024) {
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') || 'foto';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
